<script setup lang="ts">
import { readItems } from '@directus/sdk'
import { pick } from 'radash'

defineI18nRoute({
  paths: {
    en: '/prices',
    fr: '/prix',
    nl: '/prijzen',
  },
})

definePageMeta({
  auth: false,
})

const { request } = useDirectusRest()
const { locale, t } = useI18n()

const { data: blocks, refresh } = await useAsyncData(
  'price-blocks',
  () =>
    request(
      readItems('page', {
        deep: {
          blocks: {
            item: {
              translations: {
                _filter: {
                  languages_code: {
                    _eq: locale.value,
                  },
                },
              },
            },
          },
        },
        fields: [
          {
            blocks: ['collection', { item: ['*', { translations: ['*'] }] }],
          },
        ],
        filter: {
          name: 'prices',
        },
      }),
    ),
  {
    transform: (res) => {
      return res[0].blocks.map((block) => {
        return {
          ...block.item,
          component: block.collection,
          translations: block.item.translations.filter((translation) => {
            return translation.languages_code == locale.value
          }),
        }
      })
    },
    watch: [locale],
  },
)

const { data: pricing } = await useAsyncData(
  async () => {
    const pricing = await request(
      readSingleton('pricing', {
        deep: {
          durations: {
            _sort: ['duration'],
          },
        },
        fields: ['bob', 'deposit', { durations: ['duration', 'price'] }],
      }),
    )

    const drinks = await request(
      readItems('drink', {
        deep: {
          translations: {
            _filter: {
              languages_code: {
                _eq: locale.value,
              },
            },
          },
        },
        fields: ['id', 'price', { translations: ['name'] }],
        filter: {
          is_hidden: {
            _neq: true,
          },
          status: {
            _eq: 'published',
          },
        },
      }),
    )

    return {
      ...pricing,
      drinks,
    }
  },
  {
    watch: [locale],
  },
)


const { data: packages } = await useAsyncData(
  'packages-prices',
  () =>
    request(
      readItems('package', {
        deep: {
          translations: {
            _filter: {
              languages_code: {
                _eq: locale.value,
              },
            },
          },
        },
        fields: [
          'id',
          'price',
          'default_bob',
          {
            default_delivery_fee: ['id', 'price'],
            default_drink: ['id', 'price', { translations: ['name'] }],
            default_duration: ['id', 'price', 'duration'],
            translations: ['name', 'languages_code', 'description', 'weight'],
          },
        ],
        filter: {
          agency_only: false,
          status: 'published',
          locked: true
        },
        sort: ['translations.weight'],
      }),
    ),
  {
    watch: [locale],
  },
)

//This code is actualy not needed. as the page wil only display locked packages. But in case we want to display all packages, the code is already here.
const getDefaultPackagePrice = (item) => {
  const defaults = pick(item, [
    'default_drink',
    'default_bob',
    'default_duration',
    'default_delivery_fee',
  ])

  if (
    item.locked ||
    Object.entries(defaults).every(([key, value]) => !value)
  ) {
    return item.price
  }

  const duration = defaults.default_duration?.duration || 1

  const bobCost = defaults.default_bob ? pricing.value.bob * duration : 0

  return (
    defaults.default_duration?.price +
    bobCost +
    defaults.default_delivery_fee?.price +
    defaults.default_drink?.price
  )
}


</script>

<template>
  <div class="mx-auto w-full max-w-5xl px-5 lg:px-0">
    <component
      :data="block"
      :is="block.component"
      :key="`block-prices-${block.id}`"
      v-for="block in blocks"
    />
    <div class="prose !max-w-full">
      <table>
        <tbody>
          <tr>
            <th class="align-top">
              <p>{{ t('price_per_bike') }}</p>
            </th>
            <td>
              <p
                :key="`length-duration-${duration}`"
                v-for="{ duration } in pricing.durations"
              >
                {{
                  formatNumber(duration, locale, {
                    style: 'unit',
                    unit: 'hour',
                    unitDisplay: 'long',
                  })
                }}
              </p>
            </td>
            <td>
              <p
                :key="`price-duration-${duration}`"
                v-for="{ duration, price } in pricing.durations"
              >
                {{
                  formatNumber(price, locale, {
                    style: 'currency',
                    currency: 'EUR',
                  })
                }}
              </p>
            </td>
          </tr>
          <tr>
            <th class="align-top">
              <p>{{ t('price_drink') }}</p>
            </th>
            <td>
              <p
                :key="`name-drink-${drink.id}`"
                v-for="drink in pricing.drinks"
              >
                {{ drink.translations[0].name }}
              </p>
            </td>
            <td>
              <p
                :key="`price-drink-${drink.id}`"
                v-for="drink in pricing.drinks"
              >
                {{
                  formatNumber(drink.price, locale, {
                    style: 'currency',
                    currency: 'EUR',
                  })
                }}
              </p>
            </td>
          </tr>
          <tr>
            <th>
              <p>{{ t('price_bob') }}</p>
            </th>
            <td>
              <p>{{ t('per_hour') }}</p>
            </td>
            <td>
              <p>
                {{
                  formatNumber(pricing.bob, locale, {
                    style: 'currency',
                    currency: 'EUR',
                  })
                }}
              </p>
            </td>
          </tr>
          <tr>
            <th>
              <p>{{ t('price_transport') }}</p>
            </th>
            <td>
              <p>{{ t('price_transport_explanation') }}</p>
            </td>
          </tr>
          <tr v-for="ffPackage in packages" :key="ffPackage.id">
            <th>
              <p>{{ ffPackage.translations[0].name }}</p>
            </th>
            <td  
              class="prose-ul:list-none prose-ul:pl-0"  
              v-html="ffPackage.translations[0].description"
              v-if="ffPackage.translations[0].description">
            </td>
            <td>
              <p>
                {{
                  formatNumber(
                    getDefaultPackagePrice(ffPackage),
                    locale,
                    {
                      style: 'currency',
                      currency: 'EUR',
                    },
                  )
                }}
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<i18n lang="json">
{
  "nl": {
    "price_per_bike": "Feestfiets",
    "price_drink": "Drank",
    "price_bob": "BOB",
    "per_hour": "per uur",
    "price_transport": "Transport",
    "price_transport_explanation": "De transport kosten verschillen afhankelijk van de kilometers en reistijd. Deze worden weergegeven bij de stap waar u de locatie kiest. Dus voor U de aanvraag voor beschikbaarheid verstuurd, weet U exact hoeveel uw FeestFiets zou kosten."
  },
  "en": {
    "price_per_bike": "Beer bike",
    "price_drink": "Drinks",
    "price_bob": "Designated Driver",
    "per_hour": "per hour",
    "price_transport": "Transportation",
    "price_transport_explanation": "The transportation costs vary depending on the kilometers and travel time. These are displayed at the step where you choose the location. So before sending the availability request, you'll know exactly how much your BeerBike would cost."
  },
  "fr": {
    "price_per_bike": "Prix par bière vélo",
    "price_drink": "Prix pour les boissons",
    "price_bob": "Prix pour un Conducteur Désigné",
    "per_hour": "par heure",
    "price_transport": "Transport",
    "price_transport_explanation": "Les frais de transport varient en fonction des kilomètres et du temps de trajet. Ils sont affichés à l'étape où vous choisissez l'emplacement. Ainsi, avant d'envoyer la demande de disponibilité, vous saurez exactement combien coûterait votre VéloBière."
  }
}
</i18n>
